import {
  ChangeDetectionStrategy,
  Component,
  Input,
  forwardRef
} from '@angular/core';
import { Checkbox, CheckboxStyle } from 'primeng/checkbox';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule
} from '@angular/forms';

let nextCheckboxId = 0;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'm-checkbox',
  templateUrl: './m-checkbox.component.html',
  styleUrls: ['./m-checkbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MCheckboxComponent),
      multi: true
    },
    CheckboxStyle
  ],
  imports: [Checkbox, FormsModule]
})
export class MCheckboxComponent
  extends Checkbox
  implements ControlValueAccessor
{
  @Input() error = false;
  @Input() showDashedIcon = false;
  @Input() isChecked = false;

  // PrimeNG 20 applies the root host class on the host element via
  // `host: { '[class]': "cn(cx('root'), styleClass)" }`. This wrapper extends Checkbox but renders
  // its OWN inner <p-checkbox>, so that class is inherited onto <m-checkbox> too — duplicating the
  // checkbox styling. Neutralize on the wrapper host; the inner instance is unaffected.
  override cx(): string {
    return '';
  }

  private _generatedId = `m-checkbox-${nextCheckboxId++}`;
  private _modelValue: unknown;
  private _onChangeFn: (value: unknown) => void = () => undefined;
  private _onTouchedFn: () => void = () => undefined;

  get boundModel(): unknown {
    if (this.binary) {
      return this.isChecked;
    }
    return Array.isArray(this._modelValue) ? this._modelValue : [];
  }

  set boundModel(val: unknown) {
    this._onTouchedFn();
    if (this.binary) {
      this.isChecked = !!val;
      this._modelValue = this.isChecked;
      this._onChangeFn(this.isChecked);
    } else {
      const arr = Array.isArray(val) ? val : [];
      this._modelValue = arr;
      this.isChecked = arr.includes(this.value);
      this._onChangeFn(arr);
    }
  }

  /**
   * Returns the input ID for the checkbox, using the provided inputId or generating one.
   * This is used to link the label to the checkbox for click functionality.
   */
  get checkboxInputId(): string {
    return this.inputId || this._generatedId;
  }

  /**
   * Returns the styleClass for PrimeNG checkbox to apply invalid styling.
   * PrimeNG uses 'ng-invalid ng-dirty' classes to trigger invalid state styles.
   */
  get invalidStyleClass(): string {
    return this.error ? 'ng-invalid ng-dirty' : '';
  }

  override writeValue(value: unknown): void {
    this._modelValue = value;
    if (Array.isArray(value)) {
      this.isChecked = value.includes(this.value);
    } else {
      this.isChecked = !!value;
    }
    this.cd.markForCheck();
  }

  override registerOnChange(fn: (value: unknown) => void): void {
    this._onChangeFn = fn;
  }

  override registerOnTouched(fn: () => void): void {
    this._onTouchedFn = fn;
  }
}
